/*
 * Return vocabulary and helpers.
 *
 * The refund API stores one `ProductId`, one free-text `reason` and the
 * uploaded images, with a status drawn from a fixed enum. Everything the
 * UI collects therefore has to land in those same fields — the reason
 * categories, return quantity and preferred resolution are composed into
 * the single `reason` string rather than sent as new keys.
 */

/* Step 2 — the reason list shown as a radio grid. */
export const RETURN_REASONS = [
  { value: "Damaged item", hint: "Arrived broken or crushed" },
  { value: "Defective / not working", hint: "Faulty on arrival" },
  { value: "Wrong item received", hint: "Not what was ordered" },
  { value: "Not as described", hint: "Differs from the listing" },
  { value: "Changed my mind", hint: "No longer needed" },
  { value: "Other reason", hint: "Tell us in the comments" },
];

/* Step 3 — the outcomes the refund team can action. */
export const RESOLUTIONS = [
  {
    value: "Refund to original payment method",
    hint: "Back to the card or account used at checkout",
  },
  {
    value: "Store credit",
    hint: "Issued as a voucher you can spend any time",
  },
  {
    value: "Like-for-like replacement",
    hint: "Same item sent again, subject to stock",
  },
];

/*
 * Builds the free-text `reason` the backend already expects. Keeping it to
 * one readable block means the refund team sees every answer without the
 * API contract gaining a single field.
 */
export const composeReason = ({ reason, quantity, ordered, resolution, comments }) => {
  const parts = [reason];

  if (quantity) {
    parts.push(
      `Returning ${quantity} of ${ordered} unit${ordered === 1 ? "" : "s"}`
    );
  }
  if (resolution) parts.push(`Preferred resolution: ${resolution}`);

  const head = parts.filter(Boolean).join(" · ");
  const tail = comments.trim();

  return tail ? `${head}\nComments: ${tail}` : head;
};

/*
 * Status flow. The enum has five values — `pending` while the team looks at
 * the claim, three approved outcomes, and `rejected` — so the tracker shows
 * the three stages that exist rather than padding it with stages the API
 * never reports.
 */
export const RETURN_STEPS = [
  {
    key: "submitted",
    label: "Request Submitted",
    hint: "We have your claim",
  },
  {
    key: "review",
    label: "Under Review",
    hint: "Evidence being checked",
  },
  {
    key: "decision",
    label: "Decision Issued",
    hint: "Outcome confirmed",
  },
];

const isDecided = (status) => Boolean(status) && status !== "pending";

export const getReturnSteps = (status) => {
  const decided = isDecided(status);
  const currentIndex = decided ? 2 : 1;

  return RETURN_STEPS.map((step, index) => ({
    ...step,
    hint:
      step.key === "decision" && decided ? getStatusLabel(status) : step.hint,
    done: index < currentIndex,
    current: index === currentIndex,
    pending: index > currentIndex,
  }));
};

export const getReturnProgress = (status) =>
  (isDecided(status) ? 2 : 1) / (RETURN_STEPS.length - 1) * 100;

const DISPLAY_LABELS = {
  pending: "Under Review",
  "Full Refund": "Full Refund",
  "Partial Refund": "Partial Refund",
  "Item Refund": "Item Refund",
  rejected: "Rejected",
};

export const getStatusLabel = (status) =>
  DISPLAY_LABELS[status] || String(status || "pending");

export const getStatusTone = (status) => {
  if (status === "rejected") return "rejected";
  if (status === "Full Refund") return "done";
  if (isDecided(status)) return "partial";
  return "review";
};

/* Plain-English summary of the outcome, shown under the tracker. */
export const getStatusNote = (status) => {
  switch (status) {
    case "Full Refund":
      return "Approved in full. The amount goes back to your original payment method within 3–5 business days of the item reaching our warehouse.";
    case "Partial Refund":
      return "Approved in part. We'll confirm the exact amount by email before it is processed.";
    case "Item Refund":
      return "Approved as an item refund. We'll arrange the return and process it once the item has been inspected.";
    case "rejected":
      return "This request was not approved. If you think that's wrong, reply to the decision email or contact support and we'll take another look.";
    default:
      return "Our team is reviewing your claim and the evidence you supplied. Most reviews finish within 2 business days.";
  }
};

/*
 * Reads back a reason written by `composeReason`. Requests submitted before
 * this page existed are plain prose, so anything that doesn't match simply
 * comes back as the summary with no extra parts — never an error.
 */
export const parseReason = (raw) => {
  /* Multipart bodies come back with CRLF line endings — normalise first. */
  const text = String(raw || "").replace(/\r\n?/g, "\n").trim();
  if (!text) return { summary: "", quantity: null, resolution: "", comments: "" };

  const [head, ...rest] = text.split(/\n?Comments:\s*/);
  const comments = rest.join("\n").trim();
  const parts = head.split(" · ").map((part) => part.trim());

  let summary = parts[0] || "";
  let quantity = null;
  let resolution = "";

  parts.slice(1).forEach((part) => {
    const qty = part.match(/^Returning (\d+) of (\d+)/i);
    const res = part.match(/^Preferred resolution:\s*(.+)$/i);

    if (qty) quantity = { returning: Number(qty[1]), ordered: Number(qty[2]) };
    else if (res) resolution = res[1];
    else summary = `${summary} · ${part}`;
  });

  return { summary, quantity, resolution, comments };
};
